import * as React from 'react';
import { StyleSheet, Text, ActivityIndicator, ViewStyle, TextStyle, View } from 'react-native';

import color from '../../style/color';
import { font } from '../../style/text';
import { HoverableTouchableOpacity } from './HoverableComponents';

interface Props {
    children: string;
    onPress: () => any;
    disabled?: boolean;
    style?: ViewStyle;
    isLoading?: boolean;
    height?: number;
    useSecondaryColor?: boolean;
    useBlackColor?: boolean;
    textStyle?: TextStyle;
    icon?: JSX.Element;
}

export default function Button({
    children,
    onPress,
    disabled = false,
    isLoading = false,
    style = {},
    height = BUTTON_HEIGHT,
    useSecondaryColor = false,
    useBlackColor = false,
    textStyle,
    icon,
}: Props) {
    const containerStyles: [{ [key: string]: any }] = [styles.container];
    const textStyles: [{ [key: string]: any }] = [styles.text];
    if (disabled) {
        if (useBlackColor) {
            containerStyles.push(styles.containerBlackDisabled);
            textStyles.push(styles.textBlackDisabled);
        } else {
            containerStyles.push(styles.containerDisabled);
            textStyles.push(styles.textDisabled);
        }
    } else if (useSecondaryColor) {
        containerStyles.push(styles.containerSecondary);
        textStyles.push(styles.textSecondary);
    } else if (useBlackColor) {
        containerStyles.push(styles.containerBlack);
    }
    if (textStyle) textStyles.push(textStyle);
    return (
        <HoverableTouchableOpacity
            style={[containerStyles, { height, maxHeight: height, borderRadius: height }, style]}
            hoveredStyle={{ opacity: !disabled ? 0.8 : 1 }}
            {...{ disabled, onPress }}
            onPress={onPress}>
            {isLoading ? (
                <ActivityIndicator color={useSecondaryColor ? color.emerald : color.white} />
            ) : (
                <Text style={textStyles}>{children}</Text>
            )}
            {icon ? <View style={styles.containerIcon}>{icon}</View> : null}
        </HoverableTouchableOpacity>
    );
}

export const BUTTON_HEIGHT = 56;

const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        flexGrow: 1,
        borderWidth: 2,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: color.emerald,
        borderColor: color.emerald,
    },
    containerSecondary: {
        backgroundColor: color.white,
        borderColor: color.emerald,
    },
    containerBlack: {
        backgroundColor: color.black,
        borderColor: color.black,
    },
    // Disabled state of the design system's primary (black) button: `background.primaryDisabled`
    // with a `content.primaryDisabled` label — user-web-app feature/partial_design_system,
    // src/designSystem/bricks/buttons/buttonColors.ts.
    containerBlackDisabled: {
        backgroundColor: color.neutralDisabled,
        borderColor: color.neutralDisabled,
    },
    containerDisabled: {
        backgroundColor: color.frenchGray,
        borderColor: color.frenchGray,
    },
    containerIcon: {
        position: 'absolute',
        left: 25,
    },
    text: {
        fontFamily: font.ambitBlack,
        fontSize: 16,
        color: color.white,
    },
    textSecondary: {
        color: color.emerald,
    },
    textDisabled: {
        color: color.white,
    },
    textBlackDisabled: {
        color: color.neutralDisabledContent,
    },
});
